import React, { useState } from 'react';
import * as styles from './ErrorDisplay.css.ts';

interface ErrorDisplayProps {
  error: Error;
  errorInfo?: React.ErrorInfo;
  showStack?: boolean;
}

export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  error,
  errorInfo,
  showStack = true
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const getErrorText = () => {
    let text = `Error: ${error.message}\n`;
    
    if (showStack && error.stack) {
      text += `\nStack Trace:\n${error.stack}`;
    }
    
    if (errorInfo?.componentStack) {
      text += `\n\nComponent Stack:\n${errorInfo.componentStack}`;
    }
    
    return text;
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(getErrorText());
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy error:', err);
    }
  };

  const handleSelectAll = (event: React.MouseEvent<HTMLPreElement>) => {
    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(event.currentTarget);
    selection?.removeAllRanges();
    selection?.addRange(range);
  };

  return (
    <div
      className={styles.errorDisplay}
    >
      <div className={styles.header}>
        <h3 className={styles.title}>Error Occurred</h3>
        <button 
          className={styles.copyButton}
          onClick={handleCopy}
          title="Copy error details"
        >
          {isCopied ? 'Copied!' : 'Copy Error'}
        </button>
      </div>
      
      <div className={styles.errorMessage}>
        <strong>Message:</strong> {error.message}
      </div>
      
      {showStack && (error.stack || errorInfo?.componentStack) && (
        <div className={styles.stackContainer}>
          <button
            className={styles.toggleButton}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? '▼' : '▶'} Stack Trace
          </button>
          
          {isExpanded && (
            <pre 
              className={styles.stackTrace}
              onClick={handleSelectAll}
              title="Click to select all"
            >
              {error.stack && (
                <>
                  <div className={styles.stackLabel}>Error Stack:</div>
                  {error.stack}
                </>
              )}
              {errorInfo?.componentStack && (
                <>
                  <div className={styles.stackLabel}>Component Stack:</div>
                  {errorInfo.componentStack}
                </>
              )}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};